import React, { useCallback, useMemo } from 'react'
import { useCallbackBasedFormSubmitHandlers, useForm } from 'hooks/form'
import { FormProvider } from 'react-hook-form'
import { graphql, useFragment } from 'react-relay'
import { JobFormWrapper_Job$key, Position, RateMode, JobLocationTypeEnum, JobFormWrapper_Job$data } from '__generated__/JobFormWrapper_Job.graphql'

export interface IJobFormFields {
  title: string
  description: string
  description_experience: string
  description_responsibilities: string
  industry_id: number
  job_skills: JobFormWrapper_Job$data['jobSkills']
  position_types: Position[]
  freelancer_rate: number
  min_freelancer_rate: number
  client_rate: number
  min_client_rate: number
  currency: string
  required_experience_years: number
  rate_mode: RateMode
  project_length_in_months: number
  specializations: { groupIndex: number, id: number, name: string }[]
  location_type: JobLocationTypeEnum
  job_countries: { name: string, code: string }[]
  job_timezone: number
  timezone_value: number
  timezone_identifier: string
  timezone_range: number
  full_address: string
  country: string
  region: string
  city: string
  location_latitude: number
  location_longitude: number
  default_distance: number
  user_id: number
  company_name: string
  company_description: string
  company_website: string
}

export interface IJobFormWrapperProps {
  jobFragmentRef?: JobFormWrapper_Job$key
  children: React.ReactNode
}

export default function JobFormWrapper({ children, jobFragmentRef }: IJobFormWrapperProps) {
  const job = useFragment(graphql`
    fragment JobFormWrapper_Job on Job {
      slug
      firm {
        slug
      }
      title
      description
      descriptionExperience
      descriptionResponsibilities
      user {
        rawId
        name
      }
      firm {
        name
        description
        website
      }
      industry {
        rawId
      }
      jobsSpecializations {
        groupIndex
        specialization {
          rawId
          name
        }
      }
      jobSkills {
        id
        requiredYears
        required
        groupIndex
        skill {
          id
          name
        }
      }
      positionTypes
      freelancerRate {
        value
      }
      minFreelancerRate {
        value
      }
      clientRate {
        value
      }
      minClientRate {
        value
      }
      currency {
        code
      }
      requiredExperienceYears
      rateMode
      projectLengthInMonths
      locationType
      jobCountries {
        code
        name
      }
      timezoneIdentifier
      timezoneRange
      timezoneUtcOffset
      fullAddress
      country
      region
      city
      locationLatitude
      locationLongitude
      defaultDistance
    }
  `, jobFragmentRef)

  const defaultValues = useMemo<IJobFormFields>(() => ({
    title: job?.title,
    description: job?.description,
    description_experience: job?.descriptionExperience,
    description_responsibilities: job?.descriptionResponsibilities,
    industry_id: job?.industry?.rawId,
    job_skills: job?.jobSkills || [],
    position_types: job?.positionTypes as any, // TODO: fix up type mismatch
    client_rate: job?.clientRate?.value,
    min_client_rate: job?.minClientRate?.value,
    freelancer_rate: job?.freelancerRate?.value,
    min_freelancer_rate: job?.minFreelancerRate?.value,
    currency: job?.currency?.code || 'USD',
    required_experience_years: job?.requiredExperienceYears,
    rate_mode: job?.rateMode || 'hour',
    project_length_in_months: job?.projectLengthInMonths,
    specializations: job?.jobsSpecializations?.map(t => ({ id: t.specialization?.rawId, name: t.specialization?.name, groupIndex: t.groupIndex })) || [],
    location_type: job?.locationType || 'anywhere',
    job_countries: job?.jobCountries?.filter(c => c?.code && c?.name)?.map(c => ({ code: c.code, name: c.name })) || [],
    job_timezone: job?.timezoneUtcOffset,
    timezone_value: job?.timezoneUtcOffset,
    timezone_identifier: job?.timezoneIdentifier,
    timezone_range: job?.timezoneRange,
    full_address: job?.fullAddress,
    country: job?.country,
    region: job?.region,
    city: job?.city,
    location_latitude: job?.locationLatitude,
    location_longitude: job?.locationLongitude,
    default_distance: job?.defaultDistance,
    user_id: job?.user?.rawId,
    company_name: job?.firm?.name,
    company_description: job?.firm?.description,
    company_website: job?.firm?.website,
  }), [job])

  const form = useForm<IJobFormFields, { response: {}, variables: {} }>({
    defaultValues,
    submitHandlers: useCallbackBasedFormSubmitHandlers(useCallback(() => { }, [])),
  })

  return (
    <FormProvider {...form}>
      <form>
        {children}
      </form>
    </FormProvider>
  )
}
